import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { NavigationEnd, Params, Router } from '@angular/router';

import { IRemoteError } from '@partners/errors';
import { IFlatStage, INavigationOptions, NavigationService } from '@partners/navigation';
import {
  GLOBAL_SETTINGS,
  SCAN_FEATURE_NAMES,
  SettingsMap,
  TAlertFeature,
  TConfirmFeature,
  TFeature,
  TGuideFeature,
  TScanFeatures
} from '@partners/settings';
import { ShellStore } from '@partners/state';
import { cloneImmutableArray, isEmptyArray, isNotEmpty, shareReplayLast, takeOnce } from '@partners/utils';
import { Observable, Subscription, combineLatest, from } from 'rxjs';
import { filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class NavigationAdapter implements OnDestroy {
  private _router = inject(Router);
  private _shellStore = inject(ShellStore);
  private _ngZone = inject(NgZone);
  private shellConfig = inject(GLOBAL_SETTINGS);

  private _subscriptions = new Subscription();

  constructor(private navigationService: NavigationService) {
    /** listens to stages state on store, and updates the flatStages state if changes. */

    const syncNavSub = this._shellStore.policy$
      .pipe(
        /** check if we have raw stages */
        filter((policy) => !!policy?.stages?.length),
        map(({ stages }) => stages),
        /** get current flat stages collection */
        withLatestFrom(this._shellStore.flatStages$),
        /** continue to create flat stages only if not created yet */
        filter(([stages, flatStages]) => !flatStages?.length),
        /** we don't have flat stages yet, map to stages to create flat collection */
        map(([stages, flatStages]) => stages),
        /** create flat stages collection from stages collection */
        tap((stages) => {
          const flatStages = this.navigationService.syncRouter(stages);
          this.syncWithAnonymousStages(flatStages);
          this._shellStore.dispatchUpdateFlatStages(flatStages);
        }),
        shareReplayLast()
      )
      .subscribe();

    /**
     * on each navigation end event - update the shell store navigation state. the update
     * syncs the stages to the route, state and marks their state as complete | current | pristine
     **/
    const updateNavSub = this._router.events
      .pipe(
        filter((navEndEvent) => navEndEvent instanceof NavigationEnd),
        map((navEndEvent) => navEndEvent as NavigationEnd),
        withLatestFrom(this._shellStore.routeState$),
        /** avoid updates on browser refresh event */
        filter(([{ url }, { previousRoute, currentRoute }]) => {
          return url !== currentRoute?.url;
        }),
        /** the exit confirm is not recorded - the routes stay the ones it was opened from (see moveToCurrent) */
        filter(([{ url }]) => !this.#isExitConfirm(url)),
        /** update route snapshot (current & next routes) */
        tap(([{ id, url }, { previousRoute, currentRoute }]) => {
          this._shellStore.dispatchUpdateRouteNavigation({ ...currentRoute }, { id, url });
        }),
        withLatestFrom(this._shellStore.flatStages$),
        tap(([[{ id, url }, { previousRoute, currentRoute }], flatStages]) => {
          if (isEmptyArray(flatStages) && url !== '/') {
            this._shellStore.dispatchUpdateAnonymousStages({ url });
          }
        }),
        filter(([, flatStages]) => Boolean(flatStages?.length)),
        /** find a matching stage to current route */
        map(([, flatStages]) => {
          const currentStage: IFlatStage = this.navigationService.findStageByCurrentUrl(flatStages);
          return Boolean(currentStage?.name);
        }),
        filter((hasStage) => hasStage),
        /** update stages snapshot (current & next stages) */
        switchMap(() => {
          return this.#updateNavigationState();
        }),
        shareReplayLast()
      )
      .subscribe();

    this._subscriptions.add(syncNavSub);
    this._subscriptions.add(updateNavSub);

    this._shellStore.dispatchCreateAnonymousStages([]);
  }

  syncWithAnonymousStages(flatStages: IFlatStage[]) {
    from(flatStages)
      .pipe(
        takeOnce(),
        withLatestFrom(this._shellStore.anonymousStages$),
        filter(([nextStage, anonymousStages]) => !isEmptyArray(anonymousStages)),
        filter(([nextStage, anonymousStages]) => {
          const matchAnonymous = anonymousStages.find((anonymousStage) => anonymousStage.url == nextStage.url);
          return isNotEmpty(matchAnonymous);
        }),
        tap(([nextStage, anonymousStages]) => {
          this.navigationService.editStageNode(
            flatStages,
            { path: nextStage.url },
            {
              isComplete: true,
              isPristine: false,
              isCurrent: false
            }
          );
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  /**
   * this function denies navigation to already completed stages. it covers two scenarios:
   * (1) navigating by stages map - thats default behavior and mainly manages restricted
   * navigation by comparing screenCode against nextPossibleStage (2) navigating anonymously
   * by skipping from stage to stage in this case - there is no stages map and we keep track
   * after the completed routes by storing them in anonymousStages store collection, any
   * attempt to navigate to an existing route already contained in anonymousStages - is denied
   *
   * @param url the target route's url attempting to load
   * @param params any params the route carries
   * @returns a boolean value indicating if access to the route is allowed
   */
  canNavigateTo(url: string, params: Params): Observable<boolean> {
    return combineLatest([this._shellStore.flatStages$, this._shellStore.anonymousStages$]).pipe(
      takeOnce(),
      map(([flatStages, anonymousStages]) => {
        if (isEmptyArray(flatStages)) {
          /** there are no policy stages yet, validate url only against anonymous stages */
          return this.navigationService.canLoadAnonymousRoute(url, anonymousStages, params);
        }

        if (!isEmptyArray(anonymousStages)) {
          /**
           * in some cases - we might have mixed stages (both from policy and anonymous) in which case
           * we must validate the requested url against both in case we are currently on the first stage
           * from policy
           */
          const canLoadStage = this.navigationService.canLoadAnonymousRoute(url, anonymousStages, params);
          if (!canLoadStage) {
            return false;
          }
        }
        /**
         * finally - if we got here - there are no matching anonymous stage to current url AND we
         * do have policy stages
         */
        return this.navigationService.canLoadRoute(url, flatStages, params);
      })
    );
  }

  moveBack() {
    this._shellStore.navigation$
      .pipe(
        takeOnce(),
        tap(({ previousRoute, currentRoute, nextRoute }) => {
          this.navigationService.moveAny(previousRoute.url);
        })
      )
      .subscribe();
  }

  /*
  @description
  In rare cases we dont update the store with current/prev states on navigation, therefore when we want to navigate back from such scenario to the prev stage
   we actually navigate to what the stor recognizes as "current", since it hasnt changed
  @example
  navigating to "/exit" page is not recorded on the store, so when ever we leave that page to return to the app - we actually navigate to current
  */
  moveToCurrent() {
    this._shellStore.navigation$
      .pipe(
        takeOnce(),
        tap(({ currentRoute }) => {
          this.navigationService.moveAny(currentRoute.url);
        })
      )
      .subscribe();
  }

  moveNext(options?: INavigationOptions) {
    this._shellStore.flatStages$
      .pipe(
        takeOnce(),
        withLatestFrom(this._shellStore.currentStage$),
        tap(([flatStages, currentStage]) => {
          this.navigationService.moveNext(currentStage.screenCode, flatStages, options);
        })
      )
      .subscribe();
  }

  moveTo(featureName: TFeature) {
    this._shellStore.flatStages$
      .pipe(
        takeOnce(),
        tap((flatStages: IFlatStage[]) => {
          const { path, name } = SettingsMap.get(featureName);
          this.#navigate(`/${path}`);
        })
      )
      .subscribe();
  }

  toAlert(key: TAlertFeature, options?: INavigationOptions): void;

  toAlert(error: IRemoteError, options?: INavigationOptions): void;

  toAlert(nameOrError: TAlertFeature | IRemoteError, options?: any): void {
    const error = nameOrError as IRemoteError;
    if (error?.token) {
      /** go to default remote error page  */
      const settings = SettingsMap.get('generic-alert');
      return this.navigationService.moveAny(`/${settings.path}/${error.token}`);
    }

    /** go to specific (named) remote error page  */
    this.toMessage(nameOrError as TAlertFeature, options);
  }

  toGuide(key: TGuideFeature, options?: INavigationOptions) {
    this.toMessage(key, options);
  }

  toConfirm(key: TConfirmFeature, options?: INavigationOptions) {
    this.toMessage(key, options);
  }

  /**
   * @description
   * Navigates to the document scanning page if OCR scanning is enabled.
   * If OCR scanning is disabled, navigates to the identity settings page instead (determine by environment property).
   * @param {number} selectedDocId - The ID of the document to be scanned.
   */
  toScanDocument(selectedDocId: number): void {
    const { environment } = this.shellConfig;
    if (environment.isScanOcrDisabled) {
      const { name } = SettingsMap.identity();
      this.moveTo(name);
      return;
    }
    const { path } = SettingsMap.scanDocument();
    this.navigationService.moveAny(`/${path}/${selectedDocId}`);
  }

  navigateAfterScan(featureName?: TFeature): Observable<boolean> {
    return this.#resetScanRelatedStages().pipe(
      takeOnce(),
      tap(() => {
        if (!featureName) {
          this.moveNext();
        } else {
          const { name } = SettingsMap.get(featureName);
          this.moveTo(name);
        }
      }),
      map(() => true)
    );
  }

  toSimulator(): void {
    const { path } = SettingsMap.simulator();
    return this.navigationService.moveAny(`/${path}`);
  }
  toScanPreview(): void {
    const { path } = SettingsMap.scanPreview();
    return this.navigationService.moveAny(`/${path}`);
  }
  toScanMethod(): void {
    const { path } = SettingsMap.scanMethod();
    return this.navigationService.moveAny(`/${path}`);
  }
  private toMessage(key: TFeature, options?: INavigationOptions) {
    const settings = SettingsMap.get(key);
    this.navigationService.moveAny(`/${settings.path}`);
  }
  /**
   * @description
   * Scan process contains more than four several routes
   * and unlike other processes it is not linear and the process might
   * navigate from one route to another and backwards.
   * therefore we must reset the state of every scan process routes in every
   * navigation after a scan was made.
   */
  #resetScanRelatedStages(): Observable<IFlatStage[]> {
    return this._shellStore.flatStages$.pipe(
      cloneImmutableArray(),
      takeOnce(),
      filter((flatStages: IFlatStage[]) => !!flatStages?.length),
      tap((flatStages: IFlatStage[]) => {
        flatStages.forEach((stage: IFlatStage) => {
          if (SCAN_FEATURE_NAMES.has(stage.name as TScanFeatures)) {
            this.navigationService.editStageNode(
              flatStages,
              { code: stage.stageCode },
              { isComplete: false, isPristine: true, isCurrent: false }
            );
          }
        });
        this._shellStore.dispatchUpdateFlatStages(flatStages);
      })
    );
  }
  /**
   * @description
   * creates an observable that emits each time a navigate end event happens. on each emission, we want
   * to update the stages' status, namely: isCurrent, isPristine, isComplete
   *
   * First it will update the stage status on the flatStages state in the navigation store,
   * and then it will update previousStage, currentStage, and nextStage states in the navigation store.
   * @returns An observable that performs navigation state update due to stages logic.
   */
  #updateNavigationState() {
    return this._shellStore.flatStages$.pipe(
      takeOnce(),
      /**
       * we have to create an immutable clone the flat stages array, otherwise we wont be able to partially
       * update it (the store locks its value)
       */
      cloneImmutableArray(),
      withLatestFrom(this._shellStore.currentStage$),
      tap(([flatStages, lastStage]) => {
        const currentStage = this.navigationService.findStageByCurrentUrl(flatStages);
        const nextStage = this.navigationService.findStageById(flatStages, currentStage.nextPossibleStage);

        this._shellStore.dispatchUpdateStageNavigation(lastStage, currentStage, nextStage);
        const updatedFlatStages = this.navigationService.updateStagesState(flatStages);
        this._shellStore.dispatchUpdateFlatStages(updatedFlatStages);
      })
    );
  }

  #isExitConfirm(url: string): boolean {
    const { path } = SettingsMap.get('exit-confirm');
    return url === `/${path}`;
  }

  #navigate(uri: string, options: INavigationOptions = { navOutsideZone: false }) {
    const { navOutsideZone, state } = options;
    if (navOutsideZone) {
      this._ngZone.run(() => this._router.navigateByUrl(uri, { state }));
      return;
    }

    this._router.navigateByUrl(uri, { state });
  }
}
